import type { FC, MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiFlag2Line as FlagIcon } from 'react-icons/ri';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import type { FollowUpOwner } from 'src/types/followUp';
import type { FollowUpAction } from 'src/types/myFollowUp';
import StatusPill from 'src/components/followUp/TicketOverviewPage/TicketTable/Cells/StatusPill';
import { usePotentialFollowUpOwners } from 'src/components/myFollowUp/MyFollowUpPage/CreateMyFollowUpModal/Sections/SelectOwner/usePotentialFollowUpOwners';

import FollowUpDeleteButton from './components/DeleteButton';
import FollowUpDeleteModal from './components/DeleteModal';
import HeadcountPill from './components/HeadcountPill';
import ChangeOwnerFollowUpDetailModal from './ChangeOwnerFollowUpDetailModal';

import { styles } from './FollowUpDetailModalSidebar.styles';
import { styles as tableStyles } from 'src/components/followUp/TicketOverviewPage/TicketDetail/ticketDetail.styles';
import { styles as followUpDetailModalStyles } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/index.styles';

export interface FollowUpDetailModalSidebarProps {
  actionItem?: FollowUpAction;
  loading: boolean;
  disabled?: boolean;
  handleConfirmDelete: (followUpActionId: string) => void;
  userIsOwner: boolean;
}

const FollowUpDetailModalSidebar: FC<FollowUpDetailModalSidebarProps> = ({
  actionItem,
  loading,
  disabled = false,
  handleConfirmDelete,
  userIsOwner,
}) => {
  const { t } = useTranslation(['myFollowUp', 'followUp']);
  const [showAllOrgUnits, setShowAllOrgUnits] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const createdAt = useMemo(
    () => (actionItem?.createdAt ? new Date(actionItem.createdAt) : '/'),
    [actionItem],
  );

  const orgUnits = useMemo(() => {
    const orgUnits = actionItem?.orgUnits || [];
    return orgUnits.slice(0, showAllOrgUnits ? orgUnits.length : 2);
  }, [actionItem, showAllOrgUnits]);

  // get the ids from the org units in order to fetch potential follow-up owners
  // (taking care not to include the parent org units)
  const orgUnitIds = useMemo(
    () =>
      orgUnits.reduce(
        (acc: number[], orgUnits) => [...acc, orgUnits[orgUnits.length - 1].id],
        [],
      ),
    [orgUnits],
  );

  const { data } = usePotentialFollowUpOwners({
    search: '',
    orgUnitIds: orgUnitIds,
  });
  const potentialFollowUpOwners = useMemo(
    () => (data ? (data.results as FollowUpOwner[]) : undefined),
    [data],
  );

  if (loading || !actionItem) {
    return (
      <Stack direction="row" sx={{ justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const onEditOwnerClick = (e: MouseEvent<HTMLElement>) =>
    setAnchorEl((prev) => (prev ? null : e.currentTarget));

  return (
    <Box sx={{ position: 'relative', ...tableStyles.scrollContainer }}>
      <Stack direction="column" sx={{ gap: 2, p: 2 }}>
        <Typography component="h3" variant="h5">
          {t('header.details')}
        </Typography>
        <Stack direction="row" sx={styles.statusWrapper}>
          <Typography variant="body3">
            {t('actionItemHeader.status')}
          </Typography>
          <StatusPill status={actionItem.status} />
        </Stack>
      </Stack>
      {showDeleteModal && (
        <FollowUpDeleteModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          handleConfirmDelete={() => {
            handleConfirmDelete(actionItem.id.toString());
          }}
        />
      )}
      <Stack direction="column" sx={{ gap: 2, px: 2, pb: 2 }}>
        <Stack direction="row" sx={styles.statusWrapper}>
          <FollowUpDeleteButton
            onClick={() => setShowDeleteModal(true)}
            isDisabled={!actionItem.id || !userIsOwner}
          />
        </Stack>
        <Divider />
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Typography variant="body3">
            {t('actionItemHeader.followUpOwner')}
          </Typography>
          {potentialFollowUpOwners && !disabled && (
            <IconButton sx={styles.button} onClick={onEditOwnerClick}>
              <EditOutlinedIcon color="primary" fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <ChangeOwnerFollowUpDetailModal
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          followUpAction={actionItem}
          orgUnitIds={orgUnitIds}
        />
        <Typography>{actionItem.followUpOwnerName}</Typography>
        <Divider />
        <Typography variant="body3">{t('actionItemHeader.survey')}</Typography>
        <Typography variant="body1">{actionItem.surveyName}</Typography>
        <Divider />
        <Typography variant="body3">{t('header.createdDate')}</Typography>
        <Typography variant="body1">{createdAt.toLocaleString()}</Typography>
        <Divider />
        <Box>
          <Stack
            direction="row"
            sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'baseline' }}
          >
            <Typography variant="body1">{t('orgUnit.orgUnit')}</Typography>
            <HeadcountPill />
          </Stack>
          <Stack direction="column" spacing={1} sx={{ pt: 1, pb: 2 }}>
            {orgUnits.map((orgUnitPath, i) => (
              <Box key={i}>
                <Typography sx={{ fontSize: 12 }}>
                  {t('orgUnit.selected', { number: i + 1 })}
                </Typography>
                <Stack direction="row">
                  <Box
                    sx={[
                      followUpDetailModalStyles.pill,
                      ...(orgUnitPath[orgUnitPath.length - 1].priority
                        ? [followUpDetailModalStyles.priorityPill]
                        : []),
                    ]}
                  >
                    {orgUnitPath[orgUnitPath.length - 1].priority && (
                      <FlagIcon size="16px" />
                    )}
                    {orgUnitPath[orgUnitPath.length - 1].name}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Button
            endIcon={
              <ExpandMoreIcon
                color="primary"
                sx={followUpDetailModalStyles.expand(showAllOrgUnits)}
              />
            }
            variant="text"
            onClick={() => setShowAllOrgUnits((prev) => !prev)}
            sx={[styles.button, followUpDetailModalStyles.button]}
          >
            {showAllOrgUnits ? t('orgUnit.hideAll') : t('orgUnit.showAll')}
          </Button>
        </Box>
        {/* TODO: (probably eos#1223) Feature was deprioritized, add button back in when working on the download */}
        {/*<Divider />*/}
        {/*<Typography variant="body3">{t('downloadInformation')}</Typography>*/}
        {/*<Box>*/}
        {/*  <Button*/}
        {/*    variant="cta"*/}
        {/*    color="secondary"*/}
        {/*    startIcon={<DownloadRounded />}*/}
        {/*  >*/}
        {/*    {t('common:download')}*/}
        {/*  </Button>*/}
        {/*</Box>*/}
      </Stack>
    </Box>
  );
};

export default FollowUpDetailModalSidebar;
